/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { useIonRouter } from "@ionic/react";
import { IonPage, IonToast, IonCardContent } from "@ionic/react";
import {
  getMyFollowing,
  unfollowUser,
  cancelFollowRequest,
  getFollowingRequests,
  relationStats,
  pendingRequestsActionRequired,
} from "../../../services/datashare";
import Header from "../../../components/Header";
import ActionButtons from "../../../components/ui/popups/ActionButtons";
import DataSharingModal from "../datasharing/SharingModal";
import TabNavigation from "../datasharing/TabNavigation";
import FollowingList from "../datasharing/FollowingList";
import { DataSharingFollower } from "../datasharing/DataSharingTypes";
import { useStorage } from "../../../contexts/StorageContext";
import {
  getIonicStorage,
  getStoredPreferences,
  ionicStorage,
  storePrefrences,
} from "../../../services/storageService";
import { useSocket } from "../../../contexts/SocketContext";

// Following stats interface
interface FollowingStats {
  following_count: number;
  followers_count: number;
  pending_outgoing: {
    follow_requests: number;
    total: number;
  };
}

// Extended interface for internal use with display properties
interface DisplayDataSharingFollower extends DataSharingFollower {
  name: string;
  email: string;
  phone: string | null;
  dateAdded: string;
  initials: string;
}

const FollowingPage: React.FC = () => {
  // State management - only following related
  const [following, setFollowing] = useState<DisplayDataSharingFollower[]>([]);
  const [pendingOutgoing, setPendingOutgoing] = useState<
    DisplayDataSharingFollower[]
  >([]);
  const [actionRequired, setActionRequired] = useState<
    DisplayDataSharingFollower[]
  >([]);
  const [stats, setStats] = useState<FollowingStats>({
    following_count: 0,
    followers_count: 0,
    pending_outgoing: {
      follow_requests: 0,
      total: 0,
    },
  });
  const urlParams = new URLSearchParams(window.location.search);
  const openSharing = urlParams.get("openSharing") === "true";
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"followers" | "following">(
    "following"
  );
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Modal states
  const [showUnfollowModal, setShowUnfollowModal] = useState(false);
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false);
  const [selectedFollowing, setSelectedFollowing] =
    useState<DisplayDataSharingFollower | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(openSharing ?? false);

  const router = useIonRouter();
  const { storage, isStorageReady } = useStorage();
  const { socket } = useSocket();

  useEffect(() => {
    if (socket) {
      socket.on("dataSharing_refresh", () => {
        loadData(true);
      });
    }
    return () => {
      if (socket) {
        socket.off("dataSharing_refresh");
      }
    };
  }, [socket]);
  // Helper function to format follower relation data
  const formatFollowerRelation = (
    item: DataSharingFollower
  ): DisplayDataSharingFollower => {
    if (!item) {
      throw new Error("Invalid follower relation data");
    }
    // ⚠️ `profile` is optional: an item the API did not expand a person for has none. Note the
    // `?.` two lines down was already here — and it is exactly what made this dangerous: with no
    // profile both names became "", so `fullName` was falsy and control fell through to the BARE
    // `profile.phone` below. Guarding a field while trusting the object routes the null case
    // straight into the unguarded branch.
    const profile = item?.profile;

    // Handle display name
    const firstName = profile?.first_name || "";
    const lastName = profile?.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();

    let displayName: string;

    if (fullName) {
      displayName = fullName;
    } else if (profile?.phone) {
      displayName = profile.phone;
    } else {
      displayName = profile?.email || `Unkown User`;
    }

    // Handle invitation source display for invitation type
    if (item.type === "invitation" && item.invitation_source && !fullName) {
      displayName = item.invitation_source;
    }

    // Handle initials
    let initials: string;

    if (firstName || lastName) {
      const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : "";
      const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : "";
      initials = `${firstInitial}${lastInitial}`;
    } else if (profile?.username && profile.username.trim()) {
      initials = profile.username.substring(0, 2).toUpperCase();
    } else if (
      profile?.phone ||
      (item.type === "invitation" && item.invitation_source)
    ) {
      initials = "★";
    } else {
      initials = "U";
    }

    return {
      ...item,
      name: displayName,
      email: profile?.email || "",
      phone: profile?.phone || null,
      dateAdded: item.created_at,
      initials: initials,
    };
  };

  // API wrapper functions
  const fetchMyFollowing = async (): Promise<DataSharingFollower[]> => {
    try {
      const response = await getMyFollowing();
      return Array.isArray(response) ? response : (response as any)?.data || [];
    } catch (error) {
      console.error("Error fetching following:", error);
      return [];
    }
  };
  useEffect(() => {
    const fetchPendingOutgoing = async () => {
      try {
        const outgoingRequests = await pendingRequestsActionRequired();
        const formattedOutgoing = outgoingRequests.map(formatFollowerRelation);
        setActionRequired(formattedOutgoing);
        ionicStorage(storage, "action-required", formattedOutgoing);
      } catch (error) {
        console.error("Error fetching outgoing requests:", error);
      }
    };
    fetchPendingOutgoing();
  }, []);

  const fetchPendingOutgoingRequests = async (): Promise<
    DataSharingFollower[]
  > => {
    try {
      const response = await getFollowingRequests({
        request_type: "following",
      });
      return Array.isArray(response) ? response : (response as any)?.data || [];
    } catch (error) {
      console.error("Error fetching pending outgoing requests:", error);
      return [];
    }
  };

  // Load following data with cached-first approach
  const loadData = async (forceReload = false): Promise<void> => {
    setIsLoading(true);
    try {
      // 1. Load stats from Ionic Storage (fast initial display)
      let statsData = await getIonicStorage(storage, "dataSharingStats");
      if (!forceReload) {
        if (statsData) {
          setStats({
            following_count: statsData.following_count || 0,
            followers_count: statsData.followers_count || 0,
            pending_outgoing: {
              follow_requests: pendingOutgoing.length,
              total: pendingOutgoing.length,
            },
          });
        }

        // 2. Get cached data from Preferences
        const [cachedFollowing, cachedPendingOutgoing] = await Promise.all([
          getStoredPreferences("following") as Promise<
            DataSharingFollower[] | null
          >,
          getStoredPreferences("pendingFollowRequests") as Promise<
            DataSharingFollower[] | null
          >,
        ]);
        const actionRequiredData = await getIonicStorage(
          storage,
          "action-required"
        );
        if (actionRequiredData) {
          setActionRequired(actionRequiredData || []);
        }
        // 3. Show cached data if available (unless forceReload)
        if (
          !forceReload &&
          (cachedFollowing?.length ||
            cachedPendingOutgoing?.length ||
            actionRequiredData?.length > 0 ||
            stats)
        ) {
          setFollowing((cachedFollowing || []).map(formatFollowerRelation));
          setPendingOutgoing(
            (cachedPendingOutgoing || []).map(formatFollowerRelation)
          );
          setIsLoading(false);
        }
      }
      const [freshFollowing, freshPendingOutgoing] = await Promise.all([
        fetchMyFollowing(),
        fetchPendingOutgoingRequests(),
      ]);
      setFollowing(freshFollowing.map(formatFollowerRelation));
      setPendingOutgoing(freshPendingOutgoing.map(formatFollowerRelation));

      // 5. Cache fresh data
      await Promise.all([
        storePrefrences("following", freshFollowing),
        storePrefrences("pendingFollowRequests", freshPendingOutgoing),
      ]);

      // 6. Update stats with fresh API data and store in Ionic Storage
      statsData = await getStats();
      if (storage && isStorageReady) {
        await ionicStorage(storage, "dataSharingStats", statsData);
      }
      setStats({
        following_count: statsData?.following_count || 0,
        followers_count: statsData?.followers_count || 0,
        pending_outgoing: {
          follow_requests: pendingOutgoing.length,
          total: pendingOutgoing.length,
        },
      });
    } catch (error) {
      console.error("Error loading following data:", error);
      showToastMessage("Failed to load data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Utility functions
  const showToastMessage = (message: string): void => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleBack = (): void => {
    router.push("/settings", "back", "pop");
  };

  // Handle tab navigation
  const handleTabChange = (tab: "followers" | "following"): void => {
    if (tab === "followers") {
      router.push("/settings/followers", "back", "pop");
    } else {
      setActiveTab(tab);
    }
  };

  // Following management actions
  const handleUnfollow = async (
    followingUser: DisplayDataSharingFollower
  ): Promise<void> => {
    // Matches the guard MyFollowers.tsx already uses on the mirror-image action: without a
    // profile id there is nothing to unfollow, and the optimistic update below must not run.
    if (!followingUser?.profile?.id) return;
    const followingProfileId = followingUser.profile.id;

    try {
      // Optimistic update
      setFollowing(
        following.filter(
          (f: DisplayDataSharingFollower) => f.id !== followingUser.id
        )
      );

      // API call
      await unfollowUser({ following_profile_id: followingProfileId });

      showToastMessage("Unfollowed successfully");
      setShowUnfollowModal(false);
      setSelectedFollowing(null);

      // Refresh data
      await loadData(true);
    } catch (error) {
      console.error("Error unfollowing:", error);
      showToastMessage("Failed to unfollow. Please try again.");
      await loadData();
    }
  };

  // Outgoing request management
  const handleCancelRequest = async (
    following: DisplayDataSharingFollower
  ): Promise<void> => {
    if (!following) return;

    try {
      // Optimistic update
      setPendingOutgoing(pendingOutgoing.filter((f) => f.id !== following.id));

      // API call using relationship_id
      await cancelFollowRequest({
        relationship_id: following.id,
        invitation_method: following?.invitation_method,
      });

      showToastMessage("Request cancelled successfully");
      setShowCancelRequestModal(false);
      setSelectedFollowing(null);

      // Refresh data
      await loadData();
    } catch (error) {
      console.error("Error cancelling request:", error);
      showToastMessage("Failed to cancel request. Please try again.");
      await loadData();
    }
  };
  const getStats = async () => {
    const statsData = await relationStats();
    console.log("Stats Data: ", statsData);
    return statsData;
  };

  // Modal handlers
  const openUnfollowModal = (following: DisplayDataSharingFollower): void => {
    setSelectedFollowing(following);
    setShowUnfollowModal(true);
  };

  const openCancelRequestModal = (
    following: DisplayDataSharingFollower
  ): void => {
    setSelectedFollowing(following);
    setShowCancelRequestModal(true);
  };

  // Computed values
  const followingCount: number = stats.following_count || following.length;
  const pendingOutgoingCount: number =
    stats.pending_outgoing.total || pendingOutgoing.length;
  // For navigation purposes, we'll use 0 for followers since this page doesn't manage followers
  const pendingIncomingCount = 0;

  return (
    <IonPage>
      <Header
        showGreeting={false}
        showPageTitle={true}
        showUserIcon={false}
        pageTitle="Data Sharing"
        showBackButton={true}
        pageType="following"
        onBackButtonClick={handleBack}
      />
      <IonCardContent
        className="overflow-hidden"
        style={{ backgroundColor: "#F7F5FA", minHeight: "100vh" }}
      >
        {/* Tab Navigation with pending counts */}
        <TabNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          followersCount={stats.followers_count || 0}
          followingCount={stats.following_count || 0}
          pendingIncomingCount={pendingIncomingCount}
          pendingOutgoingCount={pendingOutgoingCount}
        />

        <div
          className="w-full max-w-6xl mx-auto ion-padding flex-1"
          style={{
            overflowY: "auto",
            maxHeight: "80vh",
            minHeight: "300px",
            display: "flex",
            flexDirection: "column",
            paddingBottom: "60px", // Add padding at the bottom to prevent content from being hidden behind the ActionButton
          }}
        >
          <div className="w-full">
            {/* Following Content - Only show when activeTab is following */}
            {activeTab === "following" && (
              <FollowingList
                following={following}
                pendingOutgoing={pendingOutgoing}
                actionRequired={actionRequired}
                onRemoveFollowing={openUnfollowModal}
                onCancelRequest={openCancelRequestModal}
                onInviteClick={() => setShowInviteModal(true)}
                isLoading={isLoading}
              />
            )}
          </div>

          {/* ===== MODALS ===== */}

          {/* Data Sharing Modal - Send follow requests */}
          {showInviteModal && (
            <DataSharingModal
              mode="follow"
              isOpen={showInviteModal}
              onClose={async () => {
                setShowInviteModal(false);
                await loadData();
              }}
              // F-515: shown by this page's own toast (still mounted after the popup closes),
              // not by the modal's own — see SharingModal.tsx's onSuccess doc comment.
              onSuccess={() => showToastMessage("Invite(s) sent successfully!")}
            />
          )}

          {/* Unfollow Modal */}
          {selectedFollowing && (
            <ActionButtons
              isOpen={showUnfollowModal}
              onClose={() => {
                setShowUnfollowModal(false);
                setSelectedFollowing(null);
              }}
              title="Unfollow User"
              message={`You will no longer be able to see ${selectedFollowing.name}'s data.`}
              actionButtons={[
                {
                  text: "Unfollow",
                  action: () => handleUnfollow(selectedFollowing),
                  isPrimary: true,
                },
                {
                  text: "Cancel",
                  action: () => {
                    setShowUnfollowModal(false);
                    setSelectedFollowing(null);
                  },
                },
              ]}
            />
          )}

          {/* Cancel Request Modal */}
          {selectedFollowing && (
            <ActionButtons
              isOpen={showCancelRequestModal}
              onClose={() => {
                setShowCancelRequestModal(false);
                setSelectedFollowing(null);
              }}
              title="Cancel Follow Request"
              message={`Are you sure you want to cancel your follow request to ${selectedFollowing.name}?`}
              actionButtons={[
                {
                  text: "Cancel Request",
                  action: () => handleCancelRequest(selectedFollowing),
                  isPrimary: true,
                },
                {
                  text: "Keep Request",
                  action: () => {
                    setShowCancelRequestModal(false);
                    setSelectedFollowing(null);
                  },
                },
              ]}
            />
          )}

          {/* Toast Notifications */}
          <IonToast
            isOpen={showToast}
            onDidDismiss={() => setShowToast(false)}
            message={toastMessage}
            duration={2000}
            position="bottom"
          />
        </div>
      </IonCardContent>
    </IonPage>
  );
};

export default FollowingPage;
